import type { ReactElement } from 'react';
import React, { createContext, useMemo, useState } from 'react';
import { useParams } from 'react-router';

import { HomeownerAppMenuNames } from 'src/modules/homeownerApp/components/SheetMenu';
import {
  useAiOriginalImagesQuery,
  useProfileQuery,
} from 'src/shared/graphql/generated';
import type {
  AiGeneratedImage,
  AiOriginalImage,
  User,
} from 'src/shared/graphql/generated';

export interface HomeownerAppContextProps {
  loggedIn: boolean;
  user?: Partial<User>;
  activeAiOriginalImage?: AiOriginalImage;
  aiOriginalImages: AiOriginalImage[];
  imageFileToUpload?: File;
  setImageFileToUpload: (file?: File) => void;
  fullscreenImage?: AiGeneratedImage;
  setFullscreenImage: (image?: AiGeneratedImage) => void;
  currentMenu?: HomeownerAppMenuNames;
  setCurrentMenu: (menu?: HomeownerAppMenuNames) => void;
  toolbarTabletBackground?: string;
  setToolbarTabletBackground: (color: string) => void;
}

export const initialContext = {
  loggedIn: false,
  user: undefined,
  aiOriginalImages: [],
  setImageFileToUpload: () => null,
  setFullscreenImage: () => null,
  setCurrentMenu: () => null,
  setToolbarTabletBackground: () => null,
};

const homeownerAppContext =
  createContext<HomeownerAppContextProps>(initialContext);

const HomeownerAppProvider = ({ children }: { children: ReactElement }) => {
  const { aiOriginalImageId } = useParams<{ aiOriginalImageId?: string }>();
  const [fullscreenImage, setFullscreenImage] = useState<
    AiGeneratedImage | undefined
  >();
  const [currentMenu, setCurrentMenu] = useState<HomeownerAppMenuNames>();
  const [imageFileToUpload, setImageFileToUpload] = useState<
    File | undefined
  >();
  const { data: profileData } = useProfileQuery();
  const { data: aiOriginalImageData } = useAiOriginalImagesQuery();
  const [toolbarTabletBackground, setToolbarTabletBackground] = useState<
    string | undefined
  >();

  const aiOriginalImages = useMemo(
    () => aiOriginalImageData?.aiOriginalImages || [],
    [aiOriginalImageData],
  );

  const currentUser = profileData?.profile?.user as User;
  const activeAiOriginalImage = useMemo(
    () =>
      aiOriginalImages.find(image => image.id === Number(aiOriginalImageId)),
    [aiOriginalImages, aiOriginalImageId],
  );

  const value = useMemo(
    () => ({
      user: currentUser,
      loggedIn: !!currentUser,
      activeAiOriginalImage,
      aiOriginalImages,
      imageFileToUpload,
      setImageFileToUpload,
      fullscreenImage,
      setFullscreenImage,
      currentMenu,
      setCurrentMenu,
      toolbarTabletBackground,
      setToolbarTabletBackground,
    }),
    [
      currentUser,
      activeAiOriginalImage,
      aiOriginalImages,
      fullscreenImage,
      currentMenu,
      setCurrentMenu,
      toolbarTabletBackground,
      imageFileToUpload,
      setImageFileToUpload,
    ],
  );

  return (
    // @ts-ignore
    <homeownerAppContext.Provider value={value}>
      {children}
    </homeownerAppContext.Provider>
  );
};

export { HomeownerAppProvider, homeownerAppContext, HomeownerAppMenuNames };
